/**
 * Product search components built on QueryCombobox.
 */
import { useState } from 'react'

import type {
  Product,
  ProductDerivedFields,
  ProductType,
  ProductWithStock,
} from '@snootsvet/shared'
import { getClinic, pluralizeUnit } from '@snootsvet/shared'

import { QueryCombobox } from '@/frontend/components/query-combobox'
import { useListProducts } from '@/frontend/hooks/api/use-list-products'
import { useListProductsWithStock } from '@/frontend/hooks/api/use-list-products-with-stock'

// ---------------------------------------------------------------------------
// ProductSearch
// ---------------------------------------------------------------------------

export interface ProductSearchProps {
  id?: string
  value?: (Product & ProductDerivedFields) | null
  initialValue?: (Product & ProductDerivedFields) | null
  onChange?: (product: (Product & ProductDerivedFields) | null) => void
  type?: ProductType[]
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  showClear?: boolean
  className?: string
  'aria-invalid'?: boolean
  includeInactive?: boolean
}

/** Searchable combobox for selecting a product from the catalog. */
export function ProductSearch({
  onChange,
  type,
  includeInactive,
  placeholder = 'Search or type drug name...',
  ...props
}: ProductSearchProps) {
  const [search, setSearch] = useState((props.value ?? props.initialValue)?.name ?? '')
  const query = useListProducts({
    q: search || undefined,
    limit: 50,
    active: includeInactive ? undefined : true,
    type,
    enabled: !!search,
  })

  return (
    <QueryCombobox
      query={query}
      getItems={(data) => data?.products ?? []}
      onSearchChange={setSearch}
      onValueChange={(product) => onChange?.(product ?? null)}
      isEqual={(a, b) => a.id === b.id}
      getKey={(p) => p.id}
      getLabel={(p) => p.name}
      placeholder={placeholder}
      renderItem={(product) => (
        <div className="flex flex-col">
          <span>{product.name}</span>
          <span className="text-sm text-muted-foreground">
            {product.category} · {product.unit}
          </span>
        </div>
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// ProductWithStockSearch
// ---------------------------------------------------------------------------

export interface ProductWithStockSearchProps {
  id?: string
  value?: ProductWithStock | null
  initialValue?: ProductWithStock | null
  onChange?: (product: ProductWithStock | null) => void
  type?: ProductType[]
  clinicId?: string
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  className?: string
  'aria-invalid'?: boolean
}

/**
 * Searchable combobox for selecting a product with stock data.
 *
 * Similar to `ProductSearch` but uses the stock-aware API endpoint so results
 * include per-clinic stock levels, batch numbers, and pricing. Use this when
 * the consumer needs stock data for the selected product (e.g. treatment forms).
 */
export function ProductWithStockSearch({
  onChange,
  type,
  clinicId,
  placeholder = 'Search products...',
  ...props
}: ProductWithStockSearchProps) {
  const clinicName = clinicId ? getClinic(clinicId)?.name : null

  // Seed search from the initial product name so the API returns fresh stock
  // data on mount rather than relying on a potentially stale snapshot.
  const [search, setSearch] = useState((props.value ?? props.initialValue)?.name ?? '')
  const query = useListProductsWithStock({
    q: search || undefined,
    limit: 50,
    type,
    active: true,
    enabled: !!search && !props.disabled,
  })

  return (
    <QueryCombobox
      query={query}
      getItems={(data) => data?.products ?? []}
      onSearchChange={setSearch}
      onValueChange={(product) => onChange?.(product ?? null)}
      isEqual={(a, b) => a.id === b.id}
      getKey={(p) => p.id}
      getLabel={(p) => p.name}
      debounceMs={600}
      placeholder={placeholder}
      renderItem={(product) => {
        const now = new Date()
        const clinicStock = clinicId
          ? product.stock
              .filter(
                (s) => s.clinicId === clinicId && (!s.expiryDate || new Date(s.expiryDate) > now)
              )
              .reduce((sum, s) => sum + s.quantity, 0)
          : null

        return (
          <div className="flex flex-col">
            <span>{product.name}</span>
            <span className="text-sm text-muted-foreground">
              {product.category} · {product.unit}
              {product.isControlledSubstance ? ' · CD' : ''}
              {clinicStock != null && product.type !== 'service' && (
                <>
                  {' · '}
                  <span className={clinicStock > 0 ? undefined : 'text-destructive-fg'}>
                    {clinicStock > 0
                      ? `${clinicStock} ${pluralizeUnit(product.unit, clinicStock)} at ${clinicName}`
                      : `Out of stock at ${clinicName}`}
                  </span>
                </>
              )}
            </span>
          </div>
        )
      }}
      {...props}
    />
  )
}
